import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, DownloadCloud, User as UserIcon, Clock } from 'lucide-react';
import { usersService } from '../../services/api';
import { readingSessionsService } from '../../services/api/reading-sessions.service';
import { Zone, ZoneLabels, User } from '../../types';
import { LoadingSpinner, LineChart } from '../../components/common';
import { ReadingSessionSummary, ReadingSessionBookSummaryRow } from '../../types/reading-session.types';

const groupOptions = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
  { label: 'Year', value: 'year' },
] as const;

const formatDuration = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return [hrs > 0 ? `${hrs}h` : '', mins > 0 ? `${mins}m` : '', `${secs}s`]
    .filter(Boolean)
    .join(' ');
};

export const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [summary, setSummary] = useState<ReadingSessionSummary['summary']>([]);
  const [bookTotals, setBookTotals] = useState<ReadingSessionBookSummaryRow[]>([]);
  const chartData = summary.map((row) => ({ label: row.period, value: Math.round(row.totalDurationSeconds / 60) }));
  const [totals, setTotals] = useState({ totalDurationSeconds: 0, sessions: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUser = useCallback(async () => {
    if (!id) {
      setError('Please select a student.');
      setIsLoading(false);
      return;
    }

    try {
      const userData = await usersService.getUserById(id);
      setUser(userData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load student details');
    }
  }, [id]);

  const loadSummary = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await readingSessionsService.getSummary({
        groupBy,
        userId: id,
        limit: 40,
      });

      setSummary(data.summary);
      setBookTotals(data.bookTotals ?? []);
      setTotals({ totalDurationSeconds: data.totalDurationSeconds, sessions: data.sessions });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load reading statistics');
    } finally {
      setIsLoading(false);
    }
  }, [groupBy, id]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleRefresh = () => {
    loadUser();
    loadSummary();
  };

  const handleExportCSV = () => {
    const rows = [['Period', 'ReadingTimeSeconds', 'Sessions'], ...summary.map((r) => [r.period, String(r.totalDurationSeconds), String(r.sessions)])];
    const csv = rows.map((r) => r.map((c) => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student-${id}-reading-summary.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="overflow-hidden rounded-[2rem] bg-gradient-to-r from-slate-950 via-sky-700 to-cyan-600 shadow-2xl text-white mb-10">
        <div className="p-6 sm:p-8 lg:p-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4">
              <Link to="/admin/users" className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/20">
                <ArrowLeft size={16} /> Back to users
              </Link>
              <div className="max-w-2xl">
                <p className="text-sm uppercase tracking-[0.28em] text-cyan-100/80">Student insights</p>
                <h1 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight">{user ? `${user.firstName} ${user.lastName}` : 'Student Details'}</h1>
                <p className="mt-3 text-sm sm:text-base text-slate-100/85">Review reading time, trends, and book totals for this student using a polished admin dashboard layout.</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={handleExportCSV} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg shadow-slate-900/10 transition hover:bg-slate-100">
                <DownloadCloud size={16} /> Export
              </button>
              <button onClick={handleRefresh} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-slate-900/10 transition hover:bg-white/20">
                <RefreshCw size={16} /> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="rounded-xl bg-rose-50 border border-rose-100 p-6 text-rose-800">{error}</div>
      ) : (
        user && (
          <>
            <div className="grid gap-4 lg:grid-cols-3 mb-6">
              <div className="rounded-3xl bg-white/95 p-5 shadow-xl border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                    <UserIcon size={22} />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-slate-500">Student</p>
                    <p className="mt-3 text-xl font-semibold text-slate-900">{user.firstName} {user.lastName}</p>
                    <p className="text-sm text-slate-500 mt-1">{user.email}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-3xl bg-white/95 p-5 shadow-xl border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                    <Clock size={22} />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-slate-500">Total Reading Time</p>
                    <p className="mt-3 text-2xl font-semibold text-slate-900">{formatDuration(totals.totalDurationSeconds)}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-3xl bg-white/95 p-5 shadow-xl border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                    <Clock size={22} />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-slate-500">Total Sessions</p>
                    <p className="mt-3 text-2xl font-semibold text-slate-900">{totals.sessions}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3 mb-6">
              <div className="rounded-3xl bg-white/95 p-5 shadow-xl border border-slate-200">
                <p className="text-sm uppercase tracking-[0.18em] text-slate-500">Campus</p>
                <p className="mt-3 text-xl font-semibold text-slate-900">{user.campus || 'Not assigned'}</p>
              </div>
              <div className="rounded-3xl bg-white/95 p-5 shadow-xl border border-slate-200">
                <p className="text-sm uppercase tracking-[0.18em] text-slate-500">Zone</p>
                <p className="mt-3 text-xl font-semibold text-slate-900">{user.zone ? ZoneLabels[user.zone as Zone] : 'Not assigned'}</p>
              </div>
              <div className="rounded-3xl bg-white/95 p-5 shadow-xl border border-slate-200">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Group by</span>
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value as any)}
                    className="mt-2 block w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-3 text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-200"
                  >
                    {groupOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-md mb-6 border border-slate-100">
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Reading time trend</h2>
                  <p className="text-sm text-slate-500 mt-1">See how this student’s reading time changes over the selected period.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">Grouped by {groupBy}</span>
              </div>
              <div className="mt-6">
                {summary.length > 1 ? (
                  <LineChart data={chartData} className="h-[260px]" />
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-sm text-slate-500">
                    More reading sessions are needed to show a trend chart.
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2 mb-6">
              <div className="bg-white rounded-xl p-4 shadow-md overflow-x-auto border border-slate-100">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Reading Time by Period</h2>
                <table className="w-full text-sm text-slate-700">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="pb-3 pr-6 px-2 uppercase tracking-wide">Period</th>
                      <th className="pb-3 pr-6 px-2 uppercase tracking-wide">Duration</th>
                      <th className="pb-3 px-2 uppercase tracking-wide">Sessions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {summary.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="py-8 text-center text-slate-500">No reading sessions found for this student.</td>
                      </tr>
                    ) : (
                      summary.map((row) => (
                        <tr key={row.period}>
                          <td className="py-3 pr-6 px-2 font-medium">{row.period}</td>
                          <td className="py-3 pr-6 px-2">{formatDuration(row.totalDurationSeconds)}</td>
                          <td className="py-3 px-2">{row.sessions}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-md overflow-x-auto border border-slate-100">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Reading Time by Book</h2>
                <table className="w-full text-sm text-slate-700">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="pb-3 pr-6 px-2 uppercase tracking-wide">Book</th>
                      <th className="pb-3 pr-6 px-2 uppercase tracking-wide">Duration</th>
                      <th className="pb-3 px-2 uppercase tracking-wide">Sessions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {bookTotals.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="py-8 text-center text-slate-500">No books read yet.</td>
                      </tr>
                    ) : (
                      bookTotals.map((book) => (
                        <tr key={book.bookId}>
                          <td className="py-3 pr-6 px-2 font-medium">{book.title || 'Unknown book'}</td>
                          <td className="py-3 pr-6 px-2">{formatDuration(book.totalDurationSeconds)}</td>
                          <td className="py-3 px-2">{book.sessions}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};
